import { Component } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DsButtonVariants,
  DsColors,
  DsModalService,
  DsPageMessageVariants,
  Icons,
  IconScale,
  IconStyles,
} from '@levelaccess/design-system';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { $paginationParams } from '../../../../constants/pagination-params';
import { ITenantsResponse } from '../../../../../../shared/interfaces/tenant.interface';
import { $sortingOrder } from '../../../../../../shared/constants/sort';
import { AppConfigService } from '../../../../services/app-config.service';
import { TranslateService } from '../../../../translate/translate.service';
import { TenantService } from '../../../../services/tenant.service';
import { ErrorHandlerService } from '../../../../services/error-handler.service';
import { UserAclService } from '../../../../services/user-acl.service';
import { NotificationService } from '../../../../services/notification.service';
import { LoadErrorHandler } from '../../../../services/load-error-handler';
import { PaginationService } from '../../../../services/pagination.service';
import { CommonUtility } from '../../../../utility/common.utility';
import { RoutingService } from '../../../../services/routing.service';
import { AbstractOrganizationsComponent, FormField, IOrganizationsPaginationParams } from '../abstract-organizations.component';
import { Api } from '../../../../../../shared/constants/api';
import {
  ITableDsPageMessageConfig,
  ITableEmptyState,
  TableEmptyStateType,
} from '../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { $tenant } from '../../../../../../shared/constants/tenant';
import { PackageService } from '../../../../services/package.service';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';
import { UserService } from '../../../../services/user.service';
import { FeatureFlagCollection } from '../../../../../../shared/interfaces/feature-flag.interface';

@Component({
  selector: 'app-all-organizations',
  templateUrl: './all-organizations.component.html',
  standalone: false,
})
export class AllOrganizationsComponent extends AbstractOrganizationsComponent {
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Api: typeof Api = Api;
  private tableEmptyState: ITableEmptyState;

  constructor(
    appConfigService: AppConfigService,
    translateService: TranslateService,
    tenantService: TenantService,
    formBuilder: UntypedFormBuilder,
    router: Router,
    activatedRoute: ActivatedRoute,
    errorHandlerService: ErrorHandlerService,
    userAclService: UserAclService,
    notificationService: NotificationService,
    loadErrorHandler: LoadErrorHandler,
    paginationService: PaginationService,
    routingService: RoutingService,
    packageService: PackageService,
    featureFlagService: FeatureFlagService,
    userService: UserService,
    dsModalService: DsModalService,
  ) {
    super(
      appConfigService,
      translateService,
      tenantService,
      formBuilder,
      router,
      activatedRoute,
      errorHandlerService,
      userAclService,
      notificationService,
      loadErrorHandler,
      paginationService,
      routingService,
      packageService,
      featureFlagService,
      userService,
      dsModalService,
    );

    this.buildTableEmptyState();
  }

  private buildTableEmptyState(): void {
    const currentUseDsPageMessage: boolean = Boolean(
      this.featureFlagService.variation(FeatureFlagCollection.dsPageMessage, false),
    );
    const subscription = this.featureFlagService
      .variation$(FeatureFlagCollection.dsPageMessage, false)
      .pipe(map(Boolean), startWith(currentUseDsPageMessage))
      .subscribe((useDsPageMessage: boolean) => {
        this.tableEmptyState = this.getEmptyStateConfig(useDsPageMessage);
        this.updateTableConfig();
      });

    this.subscription.add(subscription);
  }

  private getEmptyStateConfig(useDsPageMessage: boolean): ITableEmptyState {
    const title: string = this.translateService.instant('no_search_results_available_name_url');
    const subtitle: string = this.translateService.instant('no_search_results_available_description_name');

    if (!useDsPageMessage) {
      return {
        dsIcon: {
          icon: Icons.FilePlus,
          style: IconStyles.Light,
          scale: IconScale.FA_3X,
          color: DsColors.Inactive,
        },
        title,
        subtitle,
      };
    }

    const dsPageMessage: ITableDsPageMessageConfig = {
      icon: Icons.NotFoundBrand,
      iconColor: DsColors.Primary,
      iconScale: IconScale.FA_4X,
      iconStyle: IconStyles.Light,
      variant: DsPageMessageVariants.info,
    };

    return {
      type: TableEmptyStateType.dsPageMessage,
      title,
      subtitle,
      dsPageMessage,
    };
  }

  private updateTableConfig(): void {
    if (this.tableConfig) {
      this.tableConfig = this.buildTableConfig();
    }
  }

  public getTenants$(params: IOrganizationsPaginationParams): Observable<ITenantsResponse> {
    const sortingParams: Record<string, string> = CommonUtility.pickSortingParams(params, {
      [$paginationParams.sortBy]: $tenant.name,
      [$paginationParams.direction]: $sortingOrder.asc,
    });

    return this.tenantService.getTenants(
      params[$paginationParams.pageNumber],
      AbstractOrganizationsComponent.PAGINATION_PAGE_SIZE,
      params[FormField.SEARCH]?.trim() ?? '',
      sortingParams[$paginationParams.sortBy],
      sortingParams[$paginationParams.direction],
    );
  }

  public getTableEmptyState(): ITableEmptyState {
    return this.tableEmptyState;
  }
}
